import React, { useEffect } from "react";
import styles from "../modals/ResourceViewerModal.module.css";
import { resolveName, isSelfMessage, getMessageAttachments, hydrateFhirGraph } from "../utils/messageUtils";
import { renderReference, renderPriority, renderCard, getDirectionIcon } from "../utils/renderUtils";
import { useStore } from "react-redux";
import { fetchAttachment } from "../utils/fetchAttachment";
import { getIconForContentType } from "../utils/getIconForContentType";


const CommunicationCardExtended = ({ resource, handleShowNested, fhirClient, contextProvider, onReply }) => {
    const store = useStore();
    const sent = resource.sent ? new Date(resource.sent).toLocaleString() : "Unknown";
    const received = resource.received ? new Date(resource.received).toLocaleString() : "Unknown";
    const senderRef = resource.sender?.reference;
    const senderName = resolveName(senderRef, store);
    const topic = resource.topic?.coding?.[0]?.display || resource.topic?.text || "None";
    const category = resource.category?.[0]?.coding?.[0]?.display || "None";
    const status = resource.status || "unknown";
    const priority = resource.priority || "routine";
    const messages = resource.payload?.filter(p => p.contentString)?.map(p => p.contentString) || [];

    useEffect(() => {
        hydrateFhirGraph(resource, fhirClient, store);
    }, [resource]);

    return (
        <div className="p-4 border rounded shadow-sm bg-white">

            {/* ---------------- Header ---------------- */}
            <div className="mb-3 d-flex align-items-center justify-content-between">
                <h3 className="m-0 d-flex align-items-center">
                    {getDirectionIcon(
                        isSelfMessage(resource, contextProvider.id) ? "outgoing" : "incoming"
                    )}
                    <span className="ms-2">{topic}</span>
                </h3>

                <div>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                            if (onReply && typeof onReply === "function") {
                                onReply(resource);
                            } else {
                                console.log("Reply clicked for resource:", resource);
                            }
                        }}
                    >
                        Reply
                    </button>
                </div>
            </div>

            <div className="mb-2 text-muted d-flex gap-3">
                <span><strong>Priority:</strong> {renderPriority(priority.toUpperCase())}</span>
                <span><strong>Status:</strong> {status}</span>
                <span><strong>Category:</strong> {category}</span>
            </div>

            <hr />

            {/* ---------------- Participants ---------------- */}
            <div className={styles.sectionHeader}>Participants</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1">
                        <strong>Sender:</strong>{" "}
                        {renderReference(senderRef, senderName, handleShowNested)}
                    </div>

                    <div className="mb-1">
                        <strong>Recipients:</strong>{" "}
                        {resource.recipient?.length ? (
                            resource.recipient.map((rec, i) => {
                                const recRef = rec.reference;
                                const recName = resolveName(recRef, store);
                                return (
                                    <span key={recRef || i}>
                                        {renderReference(recRef, recName, handleShowNested)}
                                        {i < resource.recipient.length - 1 && ", "}
                                    </span>
                                );
                            })
                        ) : (
                            <span className="text-muted">None</span>
                        )}
                    </div>
                </div>

            </div>

            {/* ---------------- Metadata ---------------- */}
            <div className={styles.sectionHeader}>Timestamps</div>
            <div className={styles.indented}>

                <div className={styles.sectionContent}>
                    <strong>Sent:</strong> {sent} &nbsp; | &nbsp;
                    <strong>Received:</strong> {received}
                </div></div>

            {/* ---------------- Message Content ---------------- */}
            <div className={styles.sectionHeader}>Message Content</div>
            <div className={styles.indented}>
                {messages.length ? (
                    messages.map((msg, idx) => (
                        <div key={idx} className={styles.messageContent} style={{ marginBottom: "1rem" }}>
                            {msg}
                        </div>
                    ))
                ) : (
                    <div className="mb-1">No message text</div>
                )}
            </div>

            {/* ---------------- Attachments ---------------- */}
            <div className={styles.sectionHeader}>Attachments</div>
            <div className={styles.indented}>
                {(() => {
                    const attachments = getMessageAttachments(resource);
                    const state = store.getState();

                    if (!attachments.length) {
                        return <div className="text-muted">No attachments</div>;
                    }

                    return attachments.map((att, i) => {
                        // ------------------------------
                        // CASE 1: FHIR Reference
                        // ------------------------------
                        if (att.type === "reference") {
                            const ref = att.url;
                            const [type, id] = ref.split("/");
                            const name = resolveName(ref, store);

                            const fhirResource =
                                type === "DiagnosticReport" ? state.diagnosticReports?.[id] :
                                    type === "Observation" ? state.observations?.[id] :
                                        type === "DocumentReference" ? state.documentReferences?.[id] :
                                            type === "Specimen" ? state.specimens?.[id] :
                                                type === "CarePlan" ? state.carePlans?.[id] :
                                                    type === "Media" ? state.media?.[id] :
                                                      type === "Condition" ? state.conditions?.[id] :
                                                         type === "QuestionnaireResponse" ? state.questionnaireResponses?.[id] :

                                                        null;

                            return (
                                <div key={i} className="mb-3 p-2 border rounded bg-light">
                                    {fhirResource ? (
                                        <div className="ms-4">
                                            {renderCard(fhirResource, {
                                                fhirClient,
                                                handleShowNested,
                                                depth: 0
                                            })}
                                        </div>
                                    ) : (
                                        <div className="ms-4 text-muted">
                                            Loading {name || ref}...
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        // ------------------------------
                        // CASE 2: Physical File Attachment
                        // ------------------------------
                        if (att.type === "attachment") {
                            // Normalize for fetchAttachment
                            const attachmentRef = {
                                url: att.url || null,
                                data: att.data || null,
                                contentType: att.contentType || null,
                                title: att.title || "Attachment"
                            };

                            const label = attachmentRef.title;

                            return (
                                <div key={i} className="mb-3 p-2 border rounded bg-light d-flex align-items-start">
                                    {/* Icon */}
                                    <div className="me-2">
                                        {getIconForContentType(att.contentType, "CommPayload")}
                                    </div>

                                    {/* Main content */}
                                    <div className="flex-grow-1">
                                        {/* File name left, buttons right */}
                                        <div className="d-flex align-items-center justify-content-between">
                                            {/* File label */}
                                            <div className="fw-bold text-truncate me-3" style={{ maxWidth: "70%" }}>
                                                {label}{" "}
                                                <span className="text-muted">({attachmentRef.contentType || "N/A"})</span>
                                            </div>

                                            {/* Buttons aligned right */}
                                            <div className="d-flex">
                                                {/* OPEN BUTTON */}
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    onClick={async () => {
                                                        try {
                                                            const fetched = await fetchAttachment(attachmentRef, fhirClient);
                                                            if (fetched?.blob) {
                                                                const url = URL.createObjectURL(fetched.blob);
                                                                window.open(url, "_blank");
                                                            } else {
                                                                alert("Failed to open attachment.");
                                                            }
                                                        } catch (err) {
                                                            console.error("Failed to open attachment", err);
                                                            alert("Failed to open attachment.");
                                                        }
                                                    }}
                                                >
                                                    Open
                                                </button>

                                                {/* DOWNLOAD BUTTON */}
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={async () => {
                                                        try {
                                                            const fetched = await fetchAttachment(attachmentRef, fhirClient);
                                                            if (fetched?.blob) {
                                                                const blobUrl = URL.createObjectURL(fetched.blob);
                                                                const a = document.createElement("a");
                                                                a.href = blobUrl;
                                                                a.download = (attachmentRef.title || "file").replace(/\s+/g, "_");
                                                                a.click();
                                                                URL.revokeObjectURL(blobUrl);
                                                            } else {
                                                                alert("Failed to download attachment.");
                                                            }
                                                        } catch (err) {
                                                            console.error("Failed to download attachment", err);
                                                            alert("Failed to download attachment.");
                                                        }
                                                    }}
                                                >
                                                    Download
                                                </button>
                                            </div>
                                        </div>

                                        {/* Optional metadata below */}
                                        {att.source && <div className="text-muted small">{att.source}</div>}
                                        {att.creation && <div className="text-muted small">Created: {att.creation}</div>}
                                    </div>
                                </div>
                            );

                        }

                        return <div key={i} className="text-muted">Unknown attachment type</div>;
                    });
                })()}
            </div>

            {/* ---------------- Context ---------------- */}
            <div className={styles.sectionHeader}>Context</div>
            <div className="ps-5 pt-2">

                <div className="mb-1">
                    <strong>Encounter:</strong>{" "}
                    {resource.encounter?.reference ? (
                        renderReference(
                            resource.encounter.reference,
                            resolveName(resource.encounter.reference, store),
                            handleShowNested
                        )
                    ) : (
                        <span className="text-muted">None</span>
                    )}
                </div>

                {resource.basedOn?.length > 0 && (
                    <div className="mb-1">
                        <strong>Based On:</strong>{" "}
                        {resource.basedOn.map((b, i) => (
                            <span key={b.reference || i}>
                                {renderReference(b.reference, resolveName(b.reference, store), handleShowNested)}
                                {i < resource.basedOn.length - 1 && ", "}
                            </span>
                        ))}
                    </div>
                )}

                {resource.reasonCode?.length > 0 && (
                    <div className="mb-1">
                        <strong>Reason:</strong>{" "}
                        {resource.reasonCode.map(r => r.text || r.coding?.[0]?.display).join(", ")}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommunicationCardExtended;
